import CloseOutlined from '@ant-design/icons/CloseOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Form,
  Input,
  message,
  Select,
  Space,
  Steps,
  Tag,
  Tooltip,
  Upload,
} from 'antd'
import groupBy from 'lodash/groupBy'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { blue, redPrimary } from '../../../../color'
import { AvatarHasName } from '../../../../components/Common/Avatar'
import SelectPeoplePopup from '../../../../components/SelectPeoplePopup/SelectPeoplePopup'
import SpinnerInlineComponent from '../../../../components/SpinnerInlineComponent'
import { ASSIGNEE_TYPE } from '../../../../constants'
import validator from '../../../../validator'
import {
  FileAction,
  FileItem,
  SignDocumentList,
} from '../../InternalDocumentSignDocDetailPage/InternalDocumentSignDocDetailPageStyled'
import {
  CustomStepIcon,
  ModalCreateSignDocWrapper,
  StepsWrapper,
} from './PopupUpdateSignDocStyled'

const { Step } = Steps
const { Option } = Select

const PopupUpdateSignDoc = props => {
  const {
    loadingAnimationStore,
    electronicDocumentStore,
    fileStore,
    isVisiblePopupUpdateSignDoc,
    handleClosePopupUpdateSignDoc,
    selectPeopleStore,
    authenticationStore,
    signDoc,
  } = props

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    originDepartmentWithUserList,
  } = selectPeopleStore

  const [form] = Form.useForm()

  const { currentUser } = authenticationStore

  const [departmentApproverList, setDepartmentApproverList] = useState([])
  const [formApproverList, setFormApproverList] = useState([])
  const [leaderApproverList, setLeaderApproverList] = useState([])
  const [numberIssuerList, setNumberIssuerList] = useState([])
  const [digitalSignerList, setDigitalSignerList] = useState([])
  const [releaserList, setReleaserList] = useState([])

  const [stepsData, setStepsData] = useState([])

  const [fileList, setFileList] = useState([])
  const [annexAttachedFiles, setAnnexAttachedFiles] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)

  const {
    code,
    title,
    assignees,
    attachments,
    permission,
    status,
    annex_attachments,
  } = signDoc

  const creator = useMemo(
    () =>
      assignees?.filter(
        el =>
          el.assignee_type === ASSIGNEE_TYPE.USER &&
          el.permission === ASSIGNEE_TYPE.CREATE
      ),
    [assignees]
  )

  useEffect(() => {
    if (!isVisiblePopupUpdateSignDoc) return
    form.setFieldsValue({
      title: title,
    })
    if (attachments && attachments.length > 0) {
      const files = attachments.map((file, index) => ({
        uid: file.file_id || `${index}`,
        name: file.file_name,
        id: file.file_id,
      }))
      setFileList(files)
    }
    if (annex_attachments && annex_attachments.length > 0) {
      const annexFiles = annex_attachments.map((file, index) => ({
        uid: file.file_id || `${index}`,
        id: file.file_id,
        name: file.file_name,
      }))
      setAnnexAttachedFiles(annexFiles)
    }
  }, [attachments, form, isVisiblePopupUpdateSignDoc, title])

  const allSteps = useMemo(
    () => [
      {
        key: 1,
        title: 'Tạo dự thảo',
        userList:
          creator && creator.length > 0
            ? [
                {
                  image_id: creator[0].image,
                  full_name: creator[0].name_uppercase,
                },
              ]
            : [],
        allowDeleted: false,
      },
      {
        key: 2,
        title: 'Trưởng phòng duyệt',
        userList: departmentApproverList ? departmentApproverList : [],
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.DEPARTMENT_APPROVER,
        permission: 'DEPARTMENT_APPROVER',
      },
      {
        key: 3,
        title: 'Duyệt thể thức',
        userList: formApproverList ? formApproverList : [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.FORM_APPROVER,
        permission: 'FORM_APPROVER',
      },
      {
        key: 4,
        title: 'Lãnh đạo duyệt',
        userList: leaderApproverList ? leaderApproverList : [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.LEADER_APPROVER,
        permission: 'LEADER_APPROVER',
      },
      {
        key: 5,
        title: 'Cấp số',
        userList: numberIssuerList ? numberIssuerList : [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.NUMBER_ISSUER,
        permission: 'NUMBER_ISSUER',
      },
      {
        key: 6,
        title: 'Ký số điện tử',
        userList: digitalSignerList ? digitalSignerList : [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.DIGITAL_SIGNER,
        permission: 'DIGITAL_SIGNER',
      },
      {
        key: 7,
        title: 'Phát hành',
        userList: releaserList ? releaserList : [],
        type: 'approval',
        allowDeleted: false,
        assignee_type: ASSIGNEE_TYPE.RELEASER,
        permission: 'RELEASER',
      },
    ],
    [
      creator,
      departmentApproverList,
      formApproverList,
      leaderApproverList,
      numberIssuerList,
      digitalSignerList,
      releaserList,
    ]
  )

  useEffect(() => {
    if (!isVisiblePopupUpdateSignDoc || !permission || isInitialized) return

    const allowedPermissions = permission.split(',')
    const filteredSteps = allSteps.filter(
      step => !step.permission || allowedPermissions.includes(step.permission)
    )

    setStepsData(filteredSteps)
    setIsInitialized(true)
  }, [isVisiblePopupUpdateSignDoc, permission, allSteps, isInitialized])

  useEffect(() => {
    if (!isVisiblePopupUpdateSignDoc) {
      setIsInitialized(false)
    }
  }, [isVisiblePopupUpdateSignDoc])

  useEffect(() => {
    if (!isInitialized) return

    setStepsData(prevSteps =>
      prevSteps.map(step => {
        switch (step.assignee_type) {
          case ASSIGNEE_TYPE.DEPARTMENT_APPROVER:
            return { ...step, userList: departmentApproverList || [] }
          case ASSIGNEE_TYPE.FORM_APPROVER:
            return { ...step, userList: formApproverList || [] }
          case ASSIGNEE_TYPE.LEADER_APPROVER:
            return { ...step, userList: leaderApproverList || [] }
          case ASSIGNEE_TYPE.NUMBER_ISSUER:
            return { ...step, userList: numberIssuerList || [] }
          case ASSIGNEE_TYPE.DIGITAL_SIGNER:
            return { ...step, userList: digitalSignerList || [] }
          case ASSIGNEE_TYPE.RELEASER:
            return { ...step, userList: releaserList || [] }
          default:
            return step
        }
      })
    )
  }, [
    isInitialized,
    departmentApproverList,
    formApproverList,
    leaderApproverList,
    numberIssuerList,
    digitalSignerList,
    releaserList,
  ])

  useEffect(() => {
    ;(async () => {
      await selectPeopleStore.clearSelected()
    })()
  }, [])

  const {
    [ASSIGNEE_TYPE.USER]: allUsers = [],
    [ASSIGNEE_TYPE.DEPARTMENT]: departmentList = [],
    [ASSIGNEE_TYPE.GROUP]: groupList = [],
  } = useMemo(() => groupBy(assignees || [], 'assignee_type'), [assignees])

  const userList = useMemo(
    () => allUsers.filter(el => el.permission !== ASSIGNEE_TYPE.CREATE),
    [allUsers]
  )

  useEffect(() => {
    if (!isVisiblePopupUpdateSignDoc) return
    if (
      selectUserData[ASSIGNEE_TYPE.DEPARTMENT_APPROVER].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.FORM_APPROVER].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.LEADER_APPROVER].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.NUMBER_ISSUER].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.DIGITAL_SIGNER].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.RELEASER].length === 0
    ) {
      selectPeopleStore.setSelectUserData(
        selectPeopleStore.convertSelectDataObj(userList)
      )
    }
  }, [originDepartmentWithUserList, isVisiblePopupUpdateSignDoc])

  useEffect(() => {
    setDepartmentApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.DEPARTMENT_APPROVER] || []),
    ])
    setFormApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.FORM_APPROVER] || []),
    ])
    setLeaderApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.LEADER_APPROVER] || []),
    ])
    setNumberIssuerList([
      ...(selectUserData[ASSIGNEE_TYPE.NUMBER_ISSUER] || []),
    ])
    setDigitalSignerList([
      ...(selectUserData[ASSIGNEE_TYPE.DIGITAL_SIGNER] || []),
    ])
    setReleaserList([...(selectUserData[ASSIGNEE_TYPE.RELEASER] || [])])
  }, [selectUserData, selectDepartmentData, selectGroupData])

  const handleChangeFile = useCallback(
    info => {
      // Update fileList with the new list from Upload component
      const newFileList = info.fileList.map(file => ({
        ...file,
        uid: file.uid || file.file_id || `${file.name}-${Date.now()}`, // Ensure unique uid
        name: file.name || file.file_name,
        id: file.id || file.file_id,
        originFileObj: file.originFileObj || file, // Preserve original file object for uploads
      }))
      setFileList(newFileList)
    },
    [setFileList]
  )

  const handleRemoveFile = useCallback(
    uid => {
      // Remove only the file with the specified uid
      setFileList(prevFileList => prevFileList.filter(file => file.uid !== uid))
    },
    [setFileList]
  )

  const onFinish = useCallback(
    async values => {
      const uploadFileList = fileList
        .filter(
          (elem, index, fileList) =>
            fileList.findIndex(file => file.name === elem.name) === index
        )
        .filter(file => !file.id)
        .map(file => file.originFileObj)
        .filter(file => file)

      const uploadAnnexFileList = annexAttachedFiles
        .filter(
          (elem, index, fileList) =>
            fileList.findIndex(file => file.name === elem.name) === index
        )
        .filter(file => !file.id)
        .map(file => file.originFileObj)
        .filter(file => file)

      const batchUploadArr = []
      uploadFileList.forEach(file => {
        const formData = new FormData()
        formData.append('file', file)
        batchUploadArr.push(fileStore.uploadFile(formData))
      })
      uploadAnnexFileList.forEach(file => {
        const formData = new FormData()
        formData.append('file', file)
        batchUploadArr.push(fileStore.uploadFile(formData))
      })

      const new_items_assign = []
      const remove_items_assign = []
      const listAssignOrigin = [
        ...userList,
        ...departmentList,
        ...groupList,
      ].map(el => ({
        assignee_code: el.assignee_code,
        assignee_type: el.assignee_type,
        permission: el.permission,
      }))

      const currentStepPermissions = new Set(
        stepsData.filter(step => step.permission).map(step => step.permission)
      )

      const userListByPermission = {
        [ASSIGNEE_TYPE.DEPARTMENT_APPROVER]: departmentApproverList,
        [ASSIGNEE_TYPE.FORM_APPROVER]: formApproverList,
        [ASSIGNEE_TYPE.LEADER_APPROVER]: leaderApproverList,
        [ASSIGNEE_TYPE.NUMBER_ISSUER]: numberIssuerList,
        [ASSIGNEE_TYPE.DIGITAL_SIGNER]: digitalSignerList,
        [ASSIGNEE_TYPE.RELEASER]: releaserList,
        [ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER]:
          selectUserData[ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER] || [], // Add DEPARTMENT_CO_SIGNER
      }

      const listAssignAfter = Object.entries(userListByPermission)
        .filter(
          ([permission]) =>
            currentStepPermissions.has(permission) ||
            permission === ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER // Include DEPARTMENT_CO_SIGNER
        )
        .flatMap(([permission, userList]) =>
          userList.map(el => ({
            assignee_code: el.id,
            assignee_type: el.assignee_type,
            permission: el.permission || permission, // Ensure permission is set
          }))
        )

      listAssignAfter.forEach(assignAfter => {
        if (
          !listAssignOrigin.some(
            assignOrigin =>
              assignOrigin.assignee_code + assignOrigin.permission ===
              assignAfter.assignee_code + assignAfter.permission
          )
        ) {
          new_items_assign.push({
            assignee_code: assignAfter.assignee_code,
            assignee_type: assignAfter.assignee_type,
            permission: assignAfter.permission,
          })
        }
      })

      listAssignOrigin.forEach(assignOrigin => {
        if (
          !listAssignAfter.some(
            assignAfter =>
              assignAfter.assignee_code + assignAfter.permission ===
              assignOrigin.assignee_code + assignOrigin.permission
          )
        ) {
          remove_items_assign.push({
            assignee_code: assignOrigin.assignee_code,
            assignee_type: assignOrigin.assignee_type,
            permission: assignOrigin.permission,
          })
        }
      })

      const permission = stepsData
        .filter(el => el.key !== 1)
        .map(el => el.permission)
        .join(',')

      try {
        loadingAnimationStore.showSpinner(true)

        // Upload all files (both attachments and annexes)
        const responseAttachments = await Promise.all(batchUploadArr)
        const attachmentFileIds = responseAttachments
          .slice(0, uploadFileList.length)
          .map(res => res.data.file_id)

        // Handle attachments (tài liệu đính kèm)
        const new_items_attachments = []
        const remove_items_attachments = []
        const originFiles = toJS(attachments).map(file => file.file_id)
        const afterFiles = [
          ...attachmentFileIds,
          ...fileList.filter(file => file.id).map(file => file.id),
        ]

        afterFiles.forEach(fileId => {
          if (!originFiles.includes(fileId)) {
            new_items_attachments.push(fileId)
          }
        })

        originFiles.forEach(fileId => {
          if (!afterFiles.includes(fileId)) {
            remove_items_attachments.push(fileId)
          }
        })

        // Update assignees
        const dataSubmitAssign = {
          new_items: new_items_assign,
          remove_items: remove_items_assign,
          action: status,
        }
        await electronicDocumentStore.updateAssignPerson(code, dataSubmitAssign)

        // Update document with attachments and annexes
        const submitData = {
          title: values.title,
          attachments: {
            new_items: new_items_attachments,
            remove_items: remove_items_attachments,
          },
          annex_attachments: {
            new_items: [],
            remove_items: [],
          },
          permission: permission,
          action: status,
        }

        await electronicDocumentStore.updateElectronicDocument(code, submitData)
        form.resetFields()
        setFileList([])
        setAnnexAttachedFiles([])
        // Do not clear DEPARTMENT_CO_SIGNER explicitly
        selectPeopleStore.clearSelectedByType([
          ASSIGNEE_TYPE.DEPARTMENT_APPROVER,
          ASSIGNEE_TYPE.FORM_APPROVER,
          ASSIGNEE_TYPE.LEADER_APPROVER,
          ASSIGNEE_TYPE.NUMBER_ISSUER,
          ASSIGNEE_TYPE.DIGITAL_SIGNER,
          ASSIGNEE_TYPE.RELEASER,
        ]) // Clear only specific types
        electronicDocumentStore.clearFilter()
        await electronicDocumentStore.getElectronicDocumentById(code)
        handleClosePopupUpdateSignDoc()
        message.success('Chỉnh sửa thành công!')
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      fileList,
      annexAttachedFiles,
      departmentApproverList,
      formApproverList,
      leaderApproverList,
      numberIssuerList,
      digitalSignerList,
      releaserList,
      stepsData,
      selectUserData,
      code,
      status,
      attachments,
      fileStore,
      electronicDocumentStore,
      selectPeopleStore,
      form,
      handleClosePopupUpdateSignDoc,
      loadingAnimationStore,
    ]
  )

  const handleRemoveStep = useCallback(
    key => {
      const removedStep = stepsData.find(step => step.key === key)

      if (removedStep && removedStep.assignee_type) {
        selectPeopleStore.clearSelectedByType(removedStep.assignee_type)

        switch (removedStep.assignee_type) {
          case ASSIGNEE_TYPE.DEPARTMENT_APPROVER:
            setDepartmentApproverList([])
            break
          case ASSIGNEE_TYPE.FORM_APPROVER:
            setFormApproverList([])
            break
          case ASSIGNEE_TYPE.LEADER_APPROVER:
            setLeaderApproverList([])
            break
          case ASSIGNEE_TYPE.NUMBER_ISSUER:
            setNumberIssuerList([])
            break
          case ASSIGNEE_TYPE.DIGITAL_SIGNER:
            setDigitalSignerList([])
            break
          case ASSIGNEE_TYPE.RELEASER:
            setReleaserList([])
            break
        }
      }

      setStepsData(prevSteps => prevSteps.filter(step => step.key !== key))
    },
    [stepsData, selectPeopleStore]
  )

  const existingStepKeys = useCallback(() => {
    return stepsData.map(step => step.key)
  }, [stepsData])

  const handleAddStep = useCallback(
    key => {
      if (!key) return
      const stepToAdd = allSteps.find(step => step.key === key)
      if (stepToAdd) {
        const newStep = {
          ...stepToAdd,
          userList: [],
        }

        setStepsData(prev => {
          const newSteps = [...prev, newStep]
          newSteps.sort((a, b) => a.key - b.key)
          return newSteps
        })
      }
    },
    [allSteps]
  )

  const renderTitleStep = useMemo(
    () => level => (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>{level.title}</span>
        {level.key !== 1 && (
          <Tooltip title="Chọn người dùng" color={blue}>
            <Button
              style={{ marginLeft: 8 }}
              icon={<UserOutlined style={{ color: blue }} />}
              onClick={() =>
                handleOpenSelectUserDepartmentPopup(level.assignee_type)
              }
            />
          </Tooltip>
        )}
      </div>
    ),
    []
  )

  const renderDescStep = useMemo(
    () => level => {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: level.allow_edit ? 6 : 14,
            marginBottom: 14,
          }}>
          {level.userList[0] ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag
                className={'tag-selected'}
                color={'blue'}
                closable={level.key === 1 ? false : true}
                onClose={() =>
                  selectPeopleStore.handleRemoveSelect(level.userList[0])
                }>
                <AvatarHasName
                  imgId={level.userList[0]?.image_id}
                  size={22}
                  name={level.userList[0]?.full_name}
                />
              </Tag>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag style={{ borderRadius: 10 }}>Chưa gán người!</Tag>
            </div>
          )}
        </div>
      )
    },
    [selectPeopleStore]
  )

  const deletableOptions = useMemo(() => {
    const existingKeys = existingStepKeys()
    return allSteps
      .filter(item => item.allowDeleted)
      .map(step => (
        <Option
          key={step.key}
          value={step.key}
          disabled={existingKeys.includes(step.key)}>
          {step.title}
        </Option>
      ))
  }, [allSteps, existingStepKeys])

  const renderedFileList = useMemo(() => {
    return fileList.map((file, index) => (
      <FileItem key={file.uid}>
        <span>
          <FileTextOutlined style={{ marginRight: 5 }} />
          {file.name}
        </span>
        <FileAction>
          <Tooltip title="Xóa">
            <Button
              type="text"
              icon={<DeleteOutlined style={{ color: redPrimary }} />}
              onClick={() => handleRemoveFile(file.uid)}
            />
          </Tooltip>
        </FileAction>
      </FileItem>
    ))
  }, [fileList, handleRemoveFile, redPrimary])

  const renderedSteps = useMemo(() => {
    return stepsData.map(level => (
      <Step
        key={level.level}
        title={renderTitleStep(level)}
        description={renderDescStep(level)}
        icon={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CustomStepIcon>{level.key}</CustomStepIcon>
            {level.allowDeleted && (
              <CloseOutlined
                style={{ marginLeft: 6, fontSize: 12 }}
                onClick={() => handleRemoveStep(level.key)}
              />
            )}
          </div>
        }
      />
    ))
  }, [stepsData, renderTitleStep, renderDescStep, handleRemoveStep])

  return (
    <>
      <ModalCreateSignDocWrapper
        visible={isVisiblePopupUpdateSignDoc}
        footer={null}
        closable={false}
        title={'Chỉnh sửa trình ký văn bản'}
        width={1000}
        style={{ top: 60 }}>
        <SpinnerInlineComponent />
        <Form
          onFinish={onFinish}
          layout={'vertical'}
          name={'form'}
          form={form}
          initialValues={{ title: title }}>
          <Form.Item
            label={'Tiêu đề:'}
            name={'title'}
            rules={[
              { required: true, message: ' Vui lòng nhập tiêu đề trình ký!' },
              { validator: validator.validateInputString },
              { max: 200, message: 'Tiêu đề không được dài quá 200 kí tự' },
            ]}>
            <Input placeholder={'Nhập tiêu đề...'} />
          </Form.Item>
          <Form.Item label={'Tài liệu đính kèm:'} name={'file'}>
            <Upload
              valuePropName={'fileList'}
              fileList={fileList}
              multiple={true}
              beforeUpload={() => false}
              onChange={handleChangeFile}
              showUploadList={false}>
              <Button icon={<UploadOutlined />}>Chọn tài liệu đính kèm</Button>
            </Upload>
          </Form.Item>
          <SignDocumentList>{renderedFileList}</SignDocumentList>
          <div>
            <Select
              placeholder="Chọn bước để thêm"
              value={null}
              onChange={value => {
                handleAddStep(value)
              }}
              style={{ width: 300 }}>
              {deletableOptions}
            </Select>
          </div>

          <StepsWrapper count={stepsData.length} i={4}>
            <Steps
              status="process"
              className={'steps'}
              labelPlacement={'vertical'}
              size={'small'}>
              {renderedSteps}
            </Steps>
          </StepsWrapper>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 20,
            }}>
            <Space>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => {
                  form.resetFields()
                  setFileList([])
                  setAnnexAttachedFiles([])
                  selectPeopleStore.clearSelected()
                  handleClosePopupUpdateSignDoc()
                }}>
                Hủy bỏ
              </Button>
              <Button
                htmlType={'submit'}
                icon={<EditOutlined />}
                type={'primary'}>
                Chỉnh sửa
              </Button>
            </Space>
          </div>
        </Form>

        <SelectPeoplePopup
          onlyOneUser={true}
          handleCancelSelectUser={handleCancelSelectUserDepartment}
          handleSubmitSelectUser={() =>
            setIsVisibleSelectUserDepartmentPopup(false)
          }
          isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
        />
      </ModalCreateSignDocWrapper>
    </>
  )
}

PopupUpdateSignDoc.propTypes = {}

export default memo(
  inject(
    'loadingAnimationStore',
    'fileStore',
    'electronicDocumentStore',
    'selectPeopleStore',
    'signedDocumentStore',
    'authenticationStore'
  )(observer(PopupUpdateSignDoc))
)
